import i18nFactory from '@yandex-int/i18n';

import { ValidationErrorData } from '../../typings/common';
import { CampaignPlatform, CampaignType } from '../../typings/domain';
import { AdditionalGoalIds, Goal } from '../../typings/goals';
import { GdCampaignBudgetShowMode } from '../../typings/graphql-grid';
import {
    AttributionModel,
    DnaStrategyType,
    OptimizationCriteria,
    Strategy,
    StrategyErrorType,
    StrategyForEditor,
    StrategyForEditorManual,
    StrategyForEditorOptimizeClicks,
    StrategyForEditorOptimizeCpa,
    StrategyForEditorRoi
} from '../../typings/strategy';

import * as keyset from './StrategyEditorHelpers.i18n';

const i18n = i18nFactory(keyset);

const strategyNames = {
    [DnaStrategyType.MANUAL]: i18n('strategy-name-default'),
    [DnaStrategyType.OPTIMIZE_CLICKS]: i18n('strategy-name-avg-click'),
    [DnaStrategyType.OPTIMIZE_CPA]: i18n('strategy-name-avg-cpa'),
    [DnaStrategyType.ROI]: i18n('strategy-name-roi')
};

export const getStrategyNameByType = (
    strategyType: DnaStrategyType
): string => {
    const name = strategyNames[strategyType];
    if (!name) {
        throw new Error('unexpected strategy type!');
    }
    return name;
};

const strategyDescriptions = {
    [DnaStrategyType.MANUAL]: i18n('strategy-description-default'),
    [DnaStrategyType.OPTIMIZE_CLICKS]: i18n('strategy-description-avg-click'),
    [DnaStrategyType.OPTIMIZE_CPA]: i18n('strategy-description-avg-cpa'),
    [DnaStrategyType.ROI]: i18n('strategy-description-roi')
};

export const getStrategyDescriptionByType = (
    strategyType: DnaStrategyType
): string => {
    const description = strategyDescriptions[strategyType];
    if (!description) {
        throw new Error('unexpected strategy type!');
    }
    return description;
};

export const isDifferentPlatformsStrategy = (
    strategy: Pick<StrategyForEditor, 'platform'>
): boolean => strategy.platform === CampaignPlatform.BOTH;

export const isDefaultStrategyHasSeparateBidding = (
    strategy: Pick<StrategyForEditorManual, 'platform' | 'separateBidding'>
): boolean =>
    isDifferentPlatformsStrategy(strategy) && strategy.separateBidding;

export const isGoalDeleted = (
    goals: Record<string, Goal>,
    goalId?: string
): boolean =>
    Boolean(goalId) && goalId !== AdditionalGoalIds.all && !goals[goalId || ''];

const EMPTY_ERRORS_ARRAY: ValidationErrorData[] = [];

export const getStrategyFieldErrors = (
    strategy: StrategyForEditor,
    field: StrategyErrorType
): ValidationErrorData[] => {
    return strategy.errors[field] || EMPTY_ERRORS_ARRAY;
};

export const makeEmptyStrategy = (
    type: DnaStrategyType,
    platform: CampaignPlatform
): StrategyForEditor => {
    switch (type) {
        case DnaStrategyType.OPTIMIZE_CLICKS:
            return {
                type,
                platform,
                optimizationCriteria: OptimizationCriteria.AVG_CLICK,
                avgClickPrice: {
                    inputVal: ''
                },
                weeklyBudgetEnabled: false,
                weeklyBudget: {
                    inputVal: ''
                },
                avgClickPriceEnabled: false,
                maxClickPrice: {
                    inputVal: ''
                },
                weekBundle: {
                    inputVal: ''
                },
                attributionModel: AttributionModel.LAST_SIGNIFICANT_CLICK,
                errors: {}
            };

        case DnaStrategyType.OPTIMIZE_CPA:
            return {
                type,
                platform,
                goalId: AdditionalGoalIds.all,
                avgCpaPrice: {
                    inputVal: ''
                },
                weeklyBudget: {
                    inputVal: ''
                },
                maxClickPrice: {
                    inputVal: ''
                },
                attributionModel: AttributionModel.LAST_SIGNIFICANT_CLICK,
                errors: {}
            };

        case DnaStrategyType.ROI:
            return {
                type,
                platform,
                goalId: AdditionalGoalIds.all,
                roiCoef: {
                    inputVal: ''
                },
                profitability: {
                    inputVal: ''
                },
                reserveReturn: 100,
                weeklyBudget: {
                    inputVal: ''
                },
                maxClickPrice: {
                    inputVal: ''
                },
                attributionModel: AttributionModel.LAST_SIGNIFICANT_CLICK,
                errors: {}
            };

        case DnaStrategyType.MANUAL:
        default:
            return {
                type: DnaStrategyType.MANUAL,
                platform,
                attributionModel: AttributionModel.LAST_SIGNIFICANT_CLICK,
                dailyBudgetMode: GdCampaignBudgetShowMode.DEFAULT,
                dailyBudget: {
                    inputVal: ''
                },
                separateBidding: true,
                contextLimit: 100,
                cpcHoldEnabled: true,
                errors: {}
            };
    }
};

export const isStrategyManual = (
    strategy?: StrategyForEditor
): strategy is StrategyForEditorManual =>
    strategy ? strategy.type === DnaStrategyType.MANUAL : false;

export const isStrategyOptimizeClicks = (
    strategy?: StrategyForEditor
): strategy is StrategyForEditorOptimizeClicks =>
    strategy ? strategy.type === DnaStrategyType.OPTIMIZE_CLICKS : false;

export const isStrategyOptimizeCpa = (
    strategy?: StrategyForEditor
): strategy is StrategyForEditorOptimizeCpa =>
    strategy ? strategy.type === DnaStrategyType.OPTIMIZE_CPA : false;

export const isStrategyRoi = (
    strategy?: StrategyForEditor
): strategy is StrategyForEditorRoi =>
    strategy ? strategy.type === DnaStrategyType.ROI : false;

export const hasDailyBudget = isStrategyManual;

export const hasSeparateBidding = isStrategyManual;

export const hasContextLimit = isStrategyManual;

export const hasAvgClickPrice = isStrategyOptimizeClicks;

export const hasAvgClickPriceEnabled = isStrategyOptimizeClicks;

export const hasWeekBundle = isStrategyOptimizeClicks;

export const hasWeeklyBudgetEnabled = isStrategyOptimizeClicks;

export const hasOptimizationCriteria = isStrategyOptimizeClicks;

export const hasAvgCpaPrice = isStrategyOptimizeCpa;

export type StrategyWithWeeklyBudget =
    | StrategyForEditorOptimizeCpa
    | StrategyForEditorOptimizeClicks
    | StrategyForEditorRoi;

export type StrategyWithMaxClickPrice =
    | StrategyForEditorOptimizeCpa
    | StrategyForEditorOptimizeClicks
    | StrategyForEditorRoi;

export type StrategyWithGoalId =
    | StrategyForEditorOptimizeCpa
    | StrategyForEditorRoi;

export const hasWeeklyBudget = (
    strategy?: StrategyForEditor
): strategy is StrategyWithWeeklyBudget =>
    isStrategyOptimizeCpa(strategy) ||
    isStrategyOptimizeClicks(strategy) ||
    isStrategyRoi(strategy);

export const hasMaxClickPrice = (
    strategy?: StrategyForEditor
): strategy is StrategyWithMaxClickPrice =>
    isStrategyOptimizeCpa(strategy) ||
    isStrategyOptimizeClicks(strategy) ||
    isStrategyRoi(strategy);

export const hasGoalId = (
    strategy?: StrategyForEditor
): strategy is StrategyWithGoalId =>
    isStrategyOptimizeCpa(strategy) || isStrategyRoi(strategy);

export const getStrategyErrorsCount = (
    strategy: StrategyForEditor | Strategy
): number =>
    Object.keys(strategy.errors).reduce(
        (sum, key) => sum + strategy.errors[key].length,
        0
    );

const STRATEGY_TYPES_BY_CAMPAIGN_TYPE: Record<
    CampaignType,
    DnaStrategyType[]
> = {
    [CampaignType.TEXT]: [
        DnaStrategyType.ROI,
        DnaStrategyType.OPTIMIZE_CPA,
        DnaStrategyType.OPTIMIZE_CLICKS,
        DnaStrategyType.MANUAL
    ],
    [CampaignType.CONTENT_PROMOTION]: [],
    [CampaignType.CPM_BANNER]: [],
    [CampaignType.CPM_DEALS]: [],
    [CampaignType.CPM_YNDX_FRONTPAGE]: [],
    [CampaignType.DYNAMIC]: [],
    [CampaignType.INTERNAL_DISTRIB]: [],
    [CampaignType.INTERNAL_FREE]: [],
    [CampaignType.MCBANNER]: [],
    [CampaignType.MOBILE_CONTENT]: [],
    [CampaignType.PERFORMANCE]: []
};

export const getStrategyTypesByCampaignType = (
    campaignType: CampaignType
): DnaStrategyType[] => STRATEGY_TYPES_BY_CAMPAIGN_TYPE[campaignType];
